import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useAuth } from '../lib/auth'
import { useTRPC } from '../lib/trpc'
import { Button, Field, Notice, SettingBlock, TextInput, Toggle } from './index'
import { Modal } from './Modal'
import './member-data-consent.css'

/**
 * The one place member details are allowed out — and the one place that says so.
 *
 * It lives in `ui` rather than beside the settings page because it is reached
 * from two directions, and the second one is the important one: the assistant
 * offers it in the conversation where the limit was hit. A person who has just
 * been told the assistant cannot see the roster is the person who wants to
 * decide about it, and sending them to a settings page to find a switch they
 * have never seen is how a capability goes unused.
 *
 * Both entry points render the SAME modal and the same words. The consent text
 * comes from the server with the state, so what is displayed and what is
 * recorded cannot be two different paragraphs.
 */

/** Everything both entry points need, fetched once per surface. */
function useConsent(spaceId: string) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const query = useQuery(trpc.agent.memberDataConsent.queryOptions({ spaceId }))
  const set = useMutation(
    trpc.agent.setMemberDataConsent.mutationOptions({
      onSuccess: () =>
        qc.invalidateQueries({
          queryKey: trpc.agent.memberDataConsent.queryKey({ spaceId }),
        }),
    }),
  )
  return { query, set }
}

function granted(at: Date | string | null, by: string | null): string {
  if (!at) return ''
  const when = new Date(at).toLocaleDateString(undefined, {
    dateStyle: 'medium',
  })
  return by ? `Allowed by ${by} on ${when}.` : `Allowed on ${when}.`
}

/**
 * The decision itself.
 *
 * A Modal rather than a Sheet, per the rule: this is a question you answer and
 * dismiss, not a record you edit. Granting asks for the organization's name to be
 * typed — the one place in the app that does — because this is the only control
 * here whose consequence lands outside the building, on people who are not in
 * the room. Typing the name is what makes it an act rather than a click, and it
 * is also what the recorded acceptance is evidence OF.
 *
 * Withdrawing asks for nothing. Stopping data leaving is never the risky
 * direction, and friction in front of it would be friction in the wrong place.
 */
export function MemberDataConsentModal({
  spaceId,
  onClose,
  onGranted,
}: {
  spaceId: string
  onClose: () => void
  /** Called after a grant lands, so the surface that asked can carry on. */
  onGranted?: () => void
}) {
  const { query, set } = useConsent(spaceId)
  const [typed, setTyped] = useState('')
  const state = query.data
  const orgName = state?.orgName ?? ''
  const matches = typed.trim().toLowerCase() === orgName.trim().toLowerCase()

  if (!state) return null

  return (
    <Modal
      title="Member details and the assistant"
      onClose={onClose}
      footer={
        <div className="uifoot__end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!matches || set.isPending || !orgName}
            onClick={() =>
              set.mutate(
                { spaceId, granted: true, acceptedText: state.text },
                {
                  onSuccess: () => {
                    onClose()
                    onGranted?.()
                  },
                },
              )
            }
          >
            {set.isPending ? 'Recording…' : 'Allow member details'}
          </Button>
        </div>
      }
    >
      {/* The words themselves, exactly as they will be stored — split on blank
          lines and nothing else. Not markdown: this is a legal record, and a
          renderer standing between the text and the reader is a renderer that
          can change it. Paragraphs flow, so the text reads the same at 375px as
          at full width. */}
      {state.text.split('\n\n').map((paragraph) => (
        <p className="consent__text" key={paragraph.slice(0, 32)}>
          {paragraph}
        </p>
      ))}
      <div className="consent__confirm">
        <Field
          label={`Type “${orgName}” to accept`}
          info="Typed rather than clicked because this decision lands on people who are not in the room — and because what is recorded is that somebody deliberately accepted these words on the organization's behalf."
        >
          <TextInput
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            dir="auto"
          />
        </Field>
      </div>
      {set.isError && <Notice tone="error">{set.error.message}</Notice>}
    </Modal>
  )
}

/**
 * The setting, as it appears in Settings → Assistant.
 *
 * A bordered block with a toggle, because it is a statement you act on rather
 * than a value you type — and it states what is recorded underneath it, since
 * the record is the substance of the thing rather than a detail about it.
 */
export function MemberDataConsentSetting({ spaceId }: { spaceId: string }) {
  const { query, set } = useConsent(spaceId)
  const { hasPerm } = useAuth()
  const [asking, setAsking] = useState(false)
  const state = query.data
  const mayDecide = hasPerm(spaceId, 'settings.edit')

  return (
    <>
      <SettingBlock
        title="Let the assistant see member details"
        description={
          <>
            With this off the assistant still works — on counts, totals, column
            headings and the shape of a spreadsheet — but never on a particular
            person. Turning it on lets names, contact details and amounts owed
            be sent to the company that runs the AI model.
            {state?.allowed && (
              <>
                <br />
                {granted(state.grantedAt, state.grantedBy)}
              </>
            )}
            {/* Stated, not enforced: a wording change that silently switched
                the assistant off would be worse than one nobody chased. */}
            {state?.stale && (
              <>
                <br />
                The wording has been revised since this was accepted. Worth
                reading again and re-accepting.
              </>
            )}
          </>
        }
      >
        <Toggle
          checked={Boolean(state?.allowed)}
          disabled={!mayDecide || !state || set.isPending}
          onChange={(next) => {
            // On is a decision and opens the record; off is immediate.
            if (next) setAsking(true)
            else set.mutate({ spaceId, granted: false })
          }}
          label="Allow member details"
        />
      </SettingBlock>
      {asking && (
        <MemberDataConsentModal
          spaceId={spaceId}
          onClose={() => setAsking(false)}
        />
      )}
    </>
  )
}

/**
 * The offer inside the conversation.
 *
 * Whoever can decide gets the decision here, where they met the limit. Whoever
 * cannot is told who can — a button that opens a dialog you are not allowed to
 * complete is worse than a sentence.
 */
export function MemberDataConsentOffer({
  spaceId,
  onGranted,
}: {
  spaceId: string
  /**
   * How the conversation carries on once it is allowed.
   *
   * The transcript still holds the refusal, and a model reading it answers the
   * next question from that rather than trying again — "nothing has changed",
   * to an organization that has just changed it. Saying so out loud, as the
   * person's own turn, is both true and what makes it look again.
   */
  onGranted?: () => void
}) {
  const { hasPerm } = useAuth()
  const [asking, setAsking] = useState(false)

  if (!hasPerm(spaceId, 'settings.edit')) {
    return (
      <span className="asst__blocked-instead">
        Somebody who can edit this organization’s settings can allow this.
      </span>
    )
  }
  return (
    <>
      <span className="consent__offer">
        <Button variant="secondary" onClick={() => setAsking(true)}>
          Allow member details
        </Button>
      </span>
      {asking && (
        <MemberDataConsentModal
          spaceId={spaceId}
          onClose={() => setAsking(false)}
          onGranted={onGranted}
        />
      )}
    </>
  )
}
